import React, { ReactNode, useCallback, useEffect, useRef } from 'react'
import { MdClose } from 'react-icons/md'

import { ActivityIndicator } from '..'

export type TModal = {
  readonly title?: string
  readonly children: ReactNode
  readonly isOpen: boolean
  readonly isLoading?: boolean
  readonly disableClose?: boolean
  readonly onClose?: () => void
  readonly maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

export default function Modal({
  title,
  children,
  disableClose,
  isOpen,
  isLoading,
  onClose,
  maxWidth,
}: TModal) {
  const modalRef = useRef<HTMLDivElement | null>(null)

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const { target } = event

      if (modalRef.current && !modalRef.current.contains(target as Node)) {
        if (onClose) onClose()
      }
    },
    [onClose],
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  return (
    <div>
      {isOpen && (
        <>
          <div className='fixed inset-0 z-50 flex items-center justify-center'>
            <div
              ref={modalRef}
              className={`z-50 mx-5 w-full overflow-y-auto rounded-2xl bg-white shadow-lg ${
                maxWidth ? `max-w-${maxWidth}` : 'md:mx-12 lg:w-3/4 xl:w-1/2'
              }`}
            >
              <div className='flex flex-col gap-5 px-6 py-5 text-left'>
                {isLoading ? (
                  <div className='mx-auto'>
                    <ActivityIndicator size='large' />
                  </div>
                ) : (
                  <>
                    <div className='flex flex-row items-center justify-between'>
                      {title && <h1 className='text-base font-semibold'>{title}</h1>}
                      {!disableClose && (
                        <button type='button' onClick={onClose}>
                          <MdClose
                            className='ml-auto h-6 w-6 cursor-pointer text-pop-grey-600'
                            data-testid='close-button'
                            aria-label='Close Modal'
                          />
                        </button>
                      )}
                    </div>
                    {title && <hr />}
                    {children}
                  </>
                )}
              </div>
            </div>
          </div>
          <div
            className='modal-backdrop fixed inset-0 z-40 bg-gray-500 opacity-75'
            data-testid='modal-backdrop'
          />
        </>
      )}
    </div>
  )
}
